// frontend/shared-ui/src/forms/ProductFormRenderer.jsx
import React from "react";
import {
  TextField,
  NumberField,
  SelectField,
  BooleanField,
  ColorField,
} from "./fields";
import QuickQuantityField from "./fields/QuickQuantityField";

const FIELD_MAP = {
  text: TextField,
  number: NumberField,
  select: SelectField,
  boolean: BooleanField,
  color: ColorField,
};

/**
 * schema: {
 *   slug: 'odf',
 *   label: 'ODF / Patch Panels',
 *   groups: [
 *     {
 *       id: 'basic', label: 'Product Basics',
 *       columns: 2,
 *       when: (values) => boolean,          // optional — hide whole group
 *       fields: [
 *         {
 *           key, label, type,
 *           options?, required?, unit?, hint?,
 *           when: (values) => boolean,      // optional — hide this field
 *         },
 *       ],
 *     },
 *     ...
 *   ],
 *   buildSku: (v) => string,
 *   buildName: (v) => string,
 * }
 */
export default function ProductFormRenderer({
  schema,
  values = {},
  errors = {},
  onChange, // (key, value) => void
  showSkuPreview = true,
  showNamePreview = true,
}) {
  if (!schema) return null;

  const sku = schema.buildSku ? schema.buildSku(values) : "";
  const name = schema.buildName ? schema.buildName(values) : "";

  // Evaluate a `when` predicate; always true if not a function
  const passes = (when) => (typeof when === "function" ? when(values) : true);

  function QuickQtyAdapter({ field, value, onChange, error }) {
    return (
      <div className="fh-form-field">
        <QuickQuantityField
          label={field.label}
          value={value}
          min={field.min}
          presets={field.presets}
          unit={field.unit || "core"}
          unitOptions={field.unitOptions}
          onValueChange={(v) => onChange(v)}
          // No onUnitChange here — closures/ODF capacity is core-count only
          hint={field.hint}
          required={field.required}
          disabled={field.disabled}
        />
        {error && <div className="fh-form-field__error">{error}</div>}
      </div>
    );
  }

  return (
    <div className="fh-product-form">
      {(showSkuPreview || showNamePreview) && (
        <div className="fh-product-form__preview">
          {showSkuPreview && (
            <div className="fh-preview-line">
              <span className="fh-preview-label">SKU preview</span>
              <code className="fh-preview-value">{sku || "—"}</code>
            </div>
          )}
          {showNamePreview && (
            <div className="fh-preview-line">
              <span className="fh-preview-label">Name preview</span>
              <span className="fh-preview-value">{name || "—"}</span>
            </div>
          )}
        </div>
      )}

      {schema.groups.map((group) => {
        // Hide the entire group if its `when` predicate fails
        if (!passes(group.when)) return null;

        // Filter individual fields by their own `when` predicate
        const visibleFields = (group.fields || []).filter((f) =>
          passes(f.when),
        );

        // If every field was hidden, skip the group entirely
        if (visibleFields.length === 0) return null;

        return (
          <div key={group.id} className="fh-product-form__group">
            {group.label && (
              <div className="fh-product-form__group-title">{group.label}</div>
            )}
            <div
              className="fh-product-form__grid"
              style={{
                gridTemplateColumns: `repeat(${group.columns || 2}, 1fr)`,
              }}
            >
              {visibleFields.map((field) => {
                // quick-qty needs its own adapter because its prop names differ
                if (field.type === "quick-qty") {
                  return (
                    <QuickQtyAdapter
                      key={field.key}
                      field={field}
                      value={values[field.key]}
                      onChange={(v) => onChange(field.key, v)}
                      error={errors[field.key]}
                    />
                  );
                }

                const Component = FIELD_MAP[field.type] || TextField;
                return (
                  <Component
                    key={field.key}
                    field={field}
                    value={values[field.key]}
                    onChange={(v) => onChange(field.key, v)}
                    error={errors[field.key]}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
